import { StateCreator } from "zustand";

export interface ScoresheetValidationError {
  type: string;
  fieldPath?: string[];
  field_path?: string[];
  meta?: Record<string, any>;
}

export const getScoresheetValidationErrorPath = (
  error: ScoresheetValidationError,
) => {
  const meta = error.meta?.meta || error.meta;

  return (
    error.fieldPath ||
    error.field_path ||
    meta?.fieldPath ||
    meta?.field_path ||
    []
  );
};

export const getScoresheetValidationErrorMeta = (
  error: ScoresheetValidationError,
) => error.meta?.meta || error.meta || {};

export interface ScoresheetSlice {
  scoresheetCleared: boolean;
  toggleScoresheetCleared: (val: boolean) => void;
  currentScoresheetData?: Record<string, any> | null;
  setCurrentScoresheetData: (values: any) => void;
  clearCurrentScoresheetData: () => void;
  evaluationTimes: Record<string, any>;
  getEvaluationTime: (id: string) => any;
  setEvaluationTime: (id: string, value: any) => void;
  flawsDrawerOpened: boolean;
  toggleFlawsDrawer: () => void;
  styleDescriptionDrawerOpened: boolean;
  toggleStyleDescriptionDrawer: () => void;
  styleGuidelinesDrawerOpened: boolean;
  toggleStyleGuidelinesDrawer: () => void;
  disqualified: boolean;
  disqualifyEntry: () => void;
  disqualifyModalOpened: boolean;
  toggleDisqualifyModal: () => void;
  pendingDisqualificationComment: string;
  setPendingDisqualificationComment: (comment: string) => void;
  pendingDisqualificationReason: 'infected' | 'out_of_style' | 'other';
  setPendingDisqualificationReason: (
    reason: 'infected' | 'out_of_style' | 'other',
  ) => void;
  focusedScoresheetError?: Record<string, string> | null;
  setFocusedScoresheetError: (
    sectionKey: string,
    subsectionKey: string,
    fieldKey: string,
  ) => void;
  currentScoresheetSchema: any;
  setCurrentScoresheetSchema: (schema: any) => void;
  activeScoresheetSection: string;
  setActiveScoresheetSection: (section: string) => void;
  scoresheetValidationErrors: ScoresheetValidationError[];
  setScoresheetValidationErrors: (errors: ScoresheetValidationError[]) => void;
  validateCurrentScoresheet: () => Promise<boolean>;
  setValidateCurrentScoresheet: (validate?: () => Promise<boolean>) => void;
  submitCurrentScoresheet: () => Promise<void>;
  setSubmitCurrentScoresheet: (submit?: () => Promise<void>) => void;
}

export const createScoresheetSlice: StateCreator<ScoresheetSlice> = (
  set,
  get,
) => ({
  currentScoresheetData: {},
  scoresheetCleared: false,
  setCurrentScoresheetData: (values) => set({ currentScoresheetData: values }),
  toggleScoresheetCleared: (value) => set({ scoresheetCleared: value }),
  clearCurrentScoresheetData: () => {
    return set({
      scoresheetCleared: true,
      currentScoresheetData: null,
      disqualified: false,
      disqualifyModalOpened: false,
      pendingDisqualificationComment: '',
      pendingDisqualificationReason: 'infected',
      focusedScoresheetError: null,
      scoresheetValidationErrors: [],
    });
  },
  evaluationTimes: {},
  getEvaluationTime: (scoresheetId) => get()?.evaluationTimes?.[scoresheetId],
  setEvaluationTime: (scoresheetId, value) =>
    set((state) => {
      return {
        evaluationTimes: { ...state?.evaluationTimes, [scoresheetId]: value },
      };
    }),
  flawsDrawerOpened: false,
  toggleFlawsDrawer: () =>
    set((state) => ({ flawsDrawerOpened: !state.flawsDrawerOpened })),
  styleDescriptionDrawerOpened: false,
  toggleStyleDescriptionDrawer: () =>
    set((state) => ({
      styleDescriptionDrawerOpened: !state.styleDescriptionDrawerOpened,
    })),
  styleGuidelinesDrawerOpened: false,
  toggleStyleGuidelinesDrawer: () =>
    set((state) => ({
      styleGuidelinesDrawerOpened: !state.styleGuidelinesDrawerOpened,
    })),
  disqualified: false,
  disqualifyEntry: () =>
    set((state) => ({
      disqualified: !state.disqualified,
    })),
  disqualifyModalOpened: false,
  toggleDisqualifyModal: () =>
    set((state) => ({ disqualifyModalOpened: !state.disqualifyModalOpened })),
  pendingDisqualificationComment: '',
  setPendingDisqualificationComment: (comment) =>
    set({ pendingDisqualificationComment: comment }),
  pendingDisqualificationReason: 'infected',
  setPendingDisqualificationReason: (reason) =>
    set({ pendingDisqualificationReason: reason }),

  focusedScoresheetError: null,
  setFocusedScoresheetError: (sectionKey, subsectionKey, fieldKey) =>
    set(() => ({
      focusedScoresheetError: {
        section: sectionKey,
        subsection: subsectionKey,
        field: fieldKey,
      },
    })),
  currentScoresheetSchema: null,
  setCurrentScoresheetSchema: (schema) =>
    set({ currentScoresheetSchema: schema }),
  activeScoresheetSection: null,
  setActiveScoresheetSection: (section) =>
    set({ activeScoresheetSection: section }),
  scoresheetValidationErrors: [],
  setScoresheetValidationErrors: (errors) =>
    set({ scoresheetValidationErrors: errors }),
  validateCurrentScoresheet: async () => true,
  setValidateCurrentScoresheet: (validate) =>
    set({ validateCurrentScoresheet: validate || (async () => true) }),
  submitCurrentScoresheet: async () => {},
  setSubmitCurrentScoresheet: (submit) =>
    set({ submitCurrentScoresheet: submit || (async () => {}) }),
});

